import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Tooltip,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Menu,
  MenuItem,
  Popover,
  FormControl,
  Autocomplete,
  Paper,
} from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
// import DeleteOutline from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError, handleToggleError } from "../utils/errorHandler";

function AlternativeNamesPanel() {
  const [formData, setFormData] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    col_id: "",
    name: "",
    alternative_name: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteEnvId, setDeleteEnvId] = useState(null);
  const [deleteAppId, setDeleteAppId] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState({
    client_name: true,
    environment_name: true,
    application_name: true,
    col_id: true,
    name: true,
    alternative_name: true,
    // cl_id: false,
    // env_id: false,
    // app_id: false,
  });
  const [filters, setFilters] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    col_id: "",
    client_name: "",
    environment_name: "",
    application_name: "",

    name: "",
    alternative_name: "",
  });
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [tempFilters, setTempFilters] = useState({ ...filters });

  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const fetchClients = async () => {
    try {
      const data = await apiService.get("/api/alternativenames/clients");
      setClients(data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchEnvironments = async () => {
    try {
      const data = await apiService.get("/api/alternativenames/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Error fetching environments:", err);
    }
  };

  const fetchApplications = async (envId = null) => {
    try {
      let url = "/api/alternativenames/applications";
      if (envId) {
        url += `?env_id=${envId}`;
      }
      const data = await apiService.get(url);
      setApplications(data);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setApplications([]);
    }
  };

  const fetchNames = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: rowsPerPage });

    // Add search term if present
    if (searchTerm.trim() !== "") {
      params.append("search", searchTerm.trim());
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim() !== "") {
        params.append(key, value);
      }
    });

    try {
      const result = await apiService.get(
        `/api/alternativenames/paginate/list?${params.toString()}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, searchTerm]);

  useEffect(() => {
    fetchNames();
    fetchClients();
    fetchApplications(); // Load all applications initially
    fetchEnvironments();
  }, [fetchNames]);

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients, formData.cl_id]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged =
        JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);
  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    col_id: "Alternative ID",
    name: "Alternative Name",
    alternative_name: "Alternative Description",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEnvironmentChange = (event, newValue) => {
    const envId = newValue ? newValue.env_id : "";
    setFormData((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Reset application when environment changes
    }));
    setFormErrors((prev) => ({ ...prev, env_id: "" }));

    // Fetch applications for the selected environment
    if (envId) {
      fetchApplications(envId);
    } else {
      setApplications([]); // Clear applications if no environment selected
    }
  };

  const validate = () => {
    const errors = {};

    // Trim and validate
    const trimmedColId = formData.col_id?.trim() || "";
    const trimmedName = formData.name?.trim() || "";
    const trimmedAltName = formData.alternative_name?.trim() || "";

    if (!formData.env_id) errors.env_id = "Environment is required.";
    if (!formData.app_id) errors.app_id = "Application is required.";

    // Column ID validation
    if (!trimmedColId) {
      errors.col_id = "Column ID is required.";
    } else if (!/[a-zA-Z]/.test(trimmedColId)) {
      errors.col_id = "Must contain at least one letter.";
    } else if (/^[^a-zA-Z0-9]+$/.test(trimmedColId)) {
      errors.col_id = "Cannot contain only special characters.";
    }

    // Name validation
    if (!trimmedName) {
      errors.name = "Name is required.";
    } else if (!/[a-zA-Z]/.test(trimmedName)) {
      errors.name = "Must contain at least one letter.";
    } else if (/^[^a-zA-Z0-9]+$/.test(trimmedName)) {
      errors.name = "Cannot contain only special characters.";
    }

    // Alternative Name validation
    if (!trimmedAltName) {
      errors.alternative_name = "Alternative Description is required.";
    } else if (!/[a-zA-Z]/.test(trimmedAltName)) {
      errors.alternative_name = "Must contain at least one letter.";
    } else if (/^[^a-zA-Z0-9]+$/.test(trimmedAltName)) {
      errors.alternative_name = "Cannot contain only special characters.";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Trim all string values
    const trimmedData = {
      cl_id: formData.cl_id,
      env_id: formData.env_id,
      app_id: formData.app_id,
      col_id: formData.col_id.trim(),
      name: formData.name.trim(),
      alternative_name: formData.alternative_name.trim(),
    };

    try {
      // Check for duplicate col_id when adding
      if (!isEditing) {
        const duplicate = data.some(
          (item) =>
            item.col_id === trimmedData.col_id &&
            item.env_id === trimmedData.env_id &&
            item.app_id === trimmedData.app_id,
        );
        if (duplicate) {
          setFormErrors({
            col_id:
              "Column ID already exists for this environment and application.",
          });
          return;
        }
      }

      if (isEditing) {
        await apiService.put(
          `/api/alternativenames/${trimmedData.col_id}`,
          trimmedData,
        );
      } else {
        await apiService.post("/api/alternativenames", trimmedData);
      }

      setSuccessMessage(
        isEditing ? "Updated successfully!" : "Added successfully!",
      );
      setErrorMessage("");

      await fetchNames();

      setFormData({
        cl_id: "",
        env_id: "",
        app_id: "",
        col_id: "",
        name: "",
        alternative_name: "",
      });
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(""), 3000);
      handleDialogClose();
    } catch (error) {
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(error, "Alternative Name")
        : handleCreateError(error, "Alternative Name");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleEdit = (item) => {
    setOriginalFormData({ ...item });
    setFormData(item);
    setIsEditing(true);
    setIsFormChanged(false);
    setFormErrors({});

    // Load applications for the selected environment
    if (item.env_id) {
      fetchApplications(item.env_id);
    }

    setOpenDialog(true);
    setErrorMessage("");
  };

  const handleDelete = (item) => {
    setDeleteItem(item.col_id);
    setDeleteEnvId(item.env_id);
    setDeleteAppId(item.app_id);
    setOpenDeleteDialog(true);
  };

  const handleDialogOpen = () => {
    // Pre-select first client
    const defaultClient = clients.length > 0 ? clients[0].cl_id : "";
    const defaultClientName = clients.length > 0 ? clients[0].name : "";

    setFormData({
      cl_id: defaultClient,
      client_name: defaultClientName,
      env_id: "",
      app_id: "",
      col_id: "",
      name: "",
      alternative_name: "",
    });
    setFormErrors({});
    setIsEditing(false);
    setApplications([]); // Clear applications when opening new form
    setOpenDialog(true);
    setErrorMessage("");
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFormData({
      cl_id: "",
      env_id: "",
      app_id: "",
      col_id: "",
      name: "",
      alternative_name: "",
    });
    setFormErrors({});
    setErrorMessage("");
    setIsEditing(false);
    setOriginalFormData({});
    setIsFormChanged(false);
  };
  const columns = [
    "cl_id",
    "env_id",
    "app_id",
    "client_name",
    "environment_name",
    "application_name",
    "col_id",
    "name",
    "alternative_name",
  ];
  const compactMultilineStyles = {
    "& .MuiInputBase-root": {
      minHeight: "80px",
      fontSize: "0.85rem",
      padding: "0 10px",
    },
    "& .MuiInputBase-input": {
      padding: "8px 10px",
      lineHeight: "1.2",
      overflowY: "auto",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      top: "-2px",
    },
    "& .MuiInputLabel-shrink": {
      top: "0",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.875rem",
    },
  };

  const handleToggleFlag = async (col_id, env_id, app_id, active_flag) => {
    try {
      const newFlag = active_flag === 1 ? 0 : 1;

      await apiService.put(`/api/alternativenames/${col_id}`, {
        col_id,
        env_id,
        app_id,
        active_flag: newFlag,
      });

      fetchNames();

      // Show success message
      const message =
        newFlag === 1
          ? "Alternative Name Activated"
          : "Alternative Name Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      // Use centralized error handler
      const errorMessage = handleToggleError(error, "Alternative Name");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Alternative Names Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Alternative Names
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // Reset to first page when searching
              }}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={handleDialogOpen}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {/* Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "Bold",
                }}
              >
                {columnLabels[col] || col}
              </Typography>

              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#2DB0E4",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>

                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempFilters[col]}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={7} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((item) => (
                      <TableRow
                        key={`${item.cl_id}-${item.env_id}-${item.app_id}-${item.col_id}-${item.name}-${item.alternative_name}-${item.active_flag}`}
                      >
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(
                                  item.col_id,
                                  item.env_id,
                                  item.app_id,
                                  parseInt(item.active_flag),
                                )
                              }
                              title="Toggle Active Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    parseInt(item.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(item)}
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {visibleColumns.client_name && (
                          <TableCell>{item.client_name}</TableCell>
                        )}
                        {visibleColumns.environment_name && (
                          <TableCell>{item.environment_name}</TableCell>
                        )}
                        {visibleColumns.application_name && (
                          <TableCell>{item.application_name}</TableCell>
                        )}
                        {visibleColumns.col_id && (
                          <TableCell>{item.col_id}</TableCell>
                        )}
                        {visibleColumns.name && (
                          <TableCell>{item.name}</TableCell>
                        )}
                        {visibleColumns.alternative_name && (
                          <TableCell>{item.alternative_name}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={7} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No matching data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Dialog
            open={openDialog}
            // onClose={handleDialogClose}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDialog(false);
            }}
            width="small"
            maxWidth="md"
          >
            <DialogTitle
              sx={{
                fontWeight: "700",
                fontSize: "16px",
                py: 1,
              }}
            >
              {isEditing ? "Edit Alternative name" : "Add New Alternative name"}
              <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            {/* <DialogContent dividers sx={{ py: 3 }}> */}
            <DialogContent dividers sx={{ p: 2.1 }}>
              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  label="Client"
                  value={
                    formData.client_name ||
                    clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                    ""
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                     minWidth: 300,
                    backgroundColor: "white",

                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                      color: "grey",
                    },
                  }}
                  variant="outlined"
                />
                <FormControl
                  fullWidth
                  margin="none"
                  error={!!formErrors.env_id}
                >
                  <Autocomplete
                    id="env_id"
                    options={environments}
                    getOptionLabel={(option) => option.name || ""} // Display environment name
                    value={
                      environments.find(
                        (environment) => environment.env_id === formData.env_id,
                      ) || null
                    } // Match selected environment
                    onChange={handleEnvironmentChange}
                    ListboxProps={{
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          minHeight: "32px",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Environment"
                        error={!!formErrors.env_id}
                        required
                        helperText={formErrors.env_id}
                      />
                    )}
                    disabled={environments.length === 0}
                  />
                </FormControl>

                <FormControl
                  fullWidth
                  margin="none"
                  error={!!formErrors.app_id}
                >
                  <Autocomplete
                    id="app_id"
                    options={applications}
                    getOptionLabel={(option) => option.name || ""} // Display application name
                    value={
                      applications.find(
                        (application) => application.app_id === formData.app_id,
                      ) || null
                    } // Match selected application
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        app_id: newValue ? newValue.app_id : "",
                      }));
                      setFormErrors((prev) => ({ ...prev, app_id: "" }));
                    }}
                    ListboxProps={{
                      sx: {
                        
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          minHeight: "32px",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Application"
                        error={!!formErrors.app_id}
                        required
                        helperText={formErrors.app_id || !formData.env_id}
                      />
                    )}
                    disabled={!formData.env_id || applications.length === 0}
                    noOptionsText={
                      !formData.env_id
                        ? "Please select an environment first"
                        : "No applications available"
                    }
                  />
                </FormControl>
               
                <TextField
                  fullWidth
                  label="Alternative Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  variant="outlined"
                />
                
                <TextField
                  fullWidth
                  label="Alternative Description"
                  name="alternative_name"
                  value={formData.alternative_name}
                  onChange={handleChange}
                  required
                  error={!!formErrors.alternative_name}
                  helperText={formErrors.alternative_name}
                  variant="outlined"
                  multiline
                          rows={3}
                          sx={{
                            // gridColumn: "span 3",
                            ...compactMultilineStyles,
                            width: "100%",
                          }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isEditing && !isFormChanged}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openDeleteDialog}
            onClose={() => {
              setOpenDeleteDialog(false);
              setDeleteItem(null);
              setDeleteEnvId(null);
              setDeleteAppId(null);
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <Typography fontSize="0.9rem">
                Are you sure you want to delete Column{" "}
                <strong>
                  {data.find(
                    (item) =>
                      item.col_id === deleteItem &&
                      item.env_id === deleteEnvId &&
                      item.app_id === deleteAppId,
                  )?.name || deleteItem}
                </strong>{" "}
                ?
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setDeleteItem(null);
                  setDeleteEnvId(null);
                  setDeleteAppId(null);
                }}
                sx={{
                  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    await apiService.delete(
                      `/api/alternativenames/${deleteItem}?env_id=${deleteEnvId}&app_id=${deleteAppId}`,
                    );

                    setSuccessMessage("Alternative name deleted successfully!");
                    await fetchNames();
                    setErrorMessage("");
                    setOpenDeleteDialog(false);
                    setDeleteItem(null);
                    setDeleteEnvId(null);
                    setDeleteAppId(null);
                    setTimeout(() => setSuccessMessage(""), 3000);
                  } catch (err) {
                    setOpenDeleteDialog(false);
                    setDeleteItem(null);
                    setDeleteEnvId(null);
                    setDeleteAppId(null);
                    
                    // Use centralized error handler
                    const errorMessage = handleDeleteError(err, "Alternative Name");
                    setErrorMessage(errorMessage);
                    setTimeout(() => setErrorMessage(""), 5000);
                  }
                }}
                sx={{
                  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
}

export default AlternativeNamesPanel;
